import React, { useEffect, useState } from "react";
import Datatable from "../../../components/shared/datatable/Datatable";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { DeleteSvg } from "../../../svgFiles/DeleteSvg";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import EmployeeModal from "./EmployeeModal";
import {
  useRemoveEmployeeMutation,
  useSearchEmployeesQuery,
} from "./employeeApi";
import CommonDeleteModal from "../../../components/shared/modalLayout/CommonDeleteModal";
import { toast } from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";
import { convertToUserTimezone } from "../../../helpers/Utils";

const Employee = () => {
  const { _t } = useTranslator();
  const [showModal, setShowModal] = React.useState(false);
  const [modalMode, setModalMode] = React.useState("add");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const [filter, setFilter] = useState({});
  const {
    data: employees,
    isLoading,
    isError,
    error,
    refetch,
  } = useSearchEmployeesQuery({
    page,
    pageSize,
    ...filter,
  });

  const [removeEmployee, { isLoading: isDeleting }] =
    useRemoveEmployeeMutation();

  const columns = [
    { label: _t("employee_name", "Employee Name"), accessor: "name" },
    { label: _t("email", "Email"), accessor: "email" },
    { label: _t("date_of_join", "Date of Join"), accessor: "date_of_joining", cell: ({ value }) => value ? convertToUserTimezone(value) : "" },
    { label: _t("phone_number", "Phone Number"), accessor: "mobile", cell: ({ value }) => value ?? "N/A" },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => (
        <span className={value === 1 ? "cmn_badge" : "cmn_badge inactive"}>
          {value === 1 ? _t("active", "Active") : _t("inactive", "Inactive")}
        </span>
      ),
    },
    {
      label: _t("working_area", "Working Area"),
      accessor: "role.working_area",
      translate: true,
    },
    {
      label: _t("role", "Role"),
      accessor: "role.name",
      captialize: true,
      translate: true,
    },

    {
      label: _t("edit_delete", "Edit/Delete"),
      accessor: "actions",
      cell: ({ row }) => (
        <>
          <span className="cursor-pointer pe-2" onClick={() => handleEdit(row)}>
            <EditSvg />
          </span>
          <span className="cursor-pointer" onClick={() => handleDelete(row)}>
            {DeleteSvg}
          </span>
        </>
      ),
    },
  ];

  // Refetch data when filter changes
  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  // Handlers
  const handleAddEmployee = () => {
    setModalMode("add");
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setModalMode("edit");
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleDelete = (employee) => {
    setEmployeeToDelete(employee);
    setShowConfirmationModal(true);
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
    setEmployeeToDelete(null);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const confirmDelete = async () => {
    setShowConfirmationModal(false);
    try {
      if (employeeToDelete && employeeToDelete.id) {
        await removeEmployee(employeeToDelete.id).unwrap();
      } else {
        toast.error(_t("please_select_employee_first", "Please select an employee first."));
      }
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error(
        error?.data?.message || "Error deleting employee. Please try again."
      );
    } finally {
      setEmployeeToDelete(null);
    }
  };

  return (
    <div>
      <EmployeeModal
        show={showModal}
        setShow={setShowModal}
        mode={modalMode}
        employee={selectedEmployee}
      />

      <CommonDeleteModal // Add ConfirmationModal here
        show={showConfirmationModal}
        handleClose={cancelDelete}
        title="Confirm Delete"
        message= {
           _t(
              "sure_confirm_delete",
              "Are you sure you want to delete :name ?",
              { name : employeeToDelete?.name} 
            )
        }
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />      
      <Datatable
        title={_t("employees", "Employees")}
        columns={columns}
        tableData={employees?.data}
        buttons={[
          {
            label: _t("add_employee", "Add Employee"),
            onClick: handleAddEmployee,
            icon: PlusSvg,
          },
        ]}
        pagination
        page={page}
        onPageChange={handlePageChange}
        showFilter={setFilter}
        showFilterButton={true}
        filterModule="employee"
        isLoading={isLoading}
      />      
    </div>
  );
};

export default Employee;
